import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { loginRequest, forgotPassword } from "../controllers/authService";
import QuantusLogo from "../assets/img/Quantus-Invoice.png";
import QuantusMark from "../assets/img/Quantus-Invoice3.png";
import { swalError, swalSuccess, swalToast, swalConfirm } from "../utils/swal";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState(null);
  const [loadingForgot, setLoadingForgot] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // =========================
  // LOGIN
  // =========================
  // (2026-05-20) Single-session option #2: si el backend responde 409 con
  // codigo=sesion_activa, pedimos confirmacion para "Forzar inicio" y
  // reintentamos con force=true. Esto preserva la sesion del otro dispositivo
  // a menos que el usuario decida conscientemente cerrarla.
  const doLogin = async ({ force = false } = {}) => {
    const data = await loginRequest(email, password, { force });
    const userData = {
      token: data.token,
      expires_in: data.expires_in,
      // (2026-07-05) Política de vigencia de contraseña (operador/operador_admin):
      // el banner de aviso y el modal de cambio forzado la consumen.
      password_policy: data.password_policy || null,
      ...data.usuario,
    };
    login(userData);
    swalToast(`Bienvenido${data?.usuario?.nombre ? `, ${data.usuario.nombre}` : ""}`, "success");
    // (2026-07-05) Cambio obligatorio si la clave es temporal (must_change_password)
    // O si venció por la política de 180 días (password_policy.vencida).
    const passwordVencida = !!(data.password_policy && data.password_policy.vencida);
    if (data.must_change_password == false && !passwordVencida){
      navigate("/dashboard");
    } else {
      navigate("/profile?forceChangePassword=true");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await doLogin();
    } catch (err) {
      const _st = err?.response?.status;
      const _d = err?.response?.data || {};

      // --- 409 Sesion activa: ofrecer Forzar inicio ---
      // El backend envia los metadatos dentro de `detalles` para que el
      // normalizador global los preserve. data.codigo es 1409 (Err.CONFLICTO).
      const _det = _d.detalles || {};
      const _esSesionActiva =
        _st === 409 && (_det.tipo === "sesion_activa" || _det.permite_forzar === true);
      if (_esSesionActiva) {
        const _minutos = _det.sesion_iniciada_hace_minutos;
        const _textoMin =
          typeof _minutos === "number"
            ? `Hay una sesión iniciada hace ${_minutos} minuto(s) en otro equipo.`
            : "Hay una sesión activa en otro equipo.";
        const _confirmado = await swalConfirm(
          "Sesión activa en otro equipo",
          `${_textoMin}\n\nSi continúas, esa sesión se cerrará y entrarás aquí.`,
          {
            confirmText: "Forzar inicio aquí",
            cancelText: "Cancelar",
            danger: true,
          }
        );
        if (_confirmado) {
          setLoading(true);
          try {
            await doLogin({ force: true });
            return; // exito - terminamos
          } catch (err2) {
            const _st2 = err2?.response?.status;
            const _d2 = err2?.response?.data || {};
            const msg2 =
              _st2 === 401
                ? (_d2.mensaje || _d2.error || "Credenciales inválidas")
                : _st2 === 429
                ? (_d2.mensaje || _d2.error || "Demasiados intentos.")
                : (_d2.mensaje || _d2.error || "No se pudo forzar el inicio.");
            setError(msg2);
            swalError("No se pudo iniciar sesión", msg2);
            return;
          } finally {
            setLoading(false);
          }
        } else {
          // Usuario canceló: no setear error rojo, solo limpiar loading.
          setError(null);
          return;
        }
      }

      // --- Errores normales (401/429/otros) ---
      // (2026-05-17) Bug: axios expone el status en err.response.status, no
      // en err.status -> el mensaje del SweetAlert siempre era genérico.
      // Se lee el envelope nuevo {mensaje} con fallback a 'error' legacy.
      const msg =
        _st === 401
          ? (_d.mensaje || _d.error || "Credenciales inválidas")
          : _st === 429
          ? (_d.mensaje || _d.error || "Demasiados intentos. Espera un momento e intenta de nuevo.")
          : (_d.mensaje || _d.error || "Credenciales inválidas");
      setError(msg);
      swalError("No se pudo iniciar sesión", msg);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // FORGOT PASSWORD
  // =========================
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoadingForgot(true);
    setForgotMessage(null);

    try {
      const response = await forgotPassword(forgotEmail);
      const ok = response.mensaje || "Revisa tu correo para continuar.";
      setForgotMessage(ok);
      swalSuccess("Solicitud enviada", ok);
    } catch (err) {
      setForgotMessage("No se pudo procesar la solicitud.");
      swalError("No se pudo procesar la solicitud", "Intenta nuevamente en unos minutos.");
    } finally {
      setLoadingForgot(false);
    }
  };

  return (
    <main className="min-h-screen w-full grid lg:grid-cols-[1.05fr_1fr]">
      {/* ===================== PANEL IZQUIERDO (marca, nocturno) ===================== */}
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-sirius-night p-12">
        <div className="absolute inset-0 bg-sirius-mesh opacity-50" />
        <div className="pointer-events-none absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-spark-500/25 blur-[120px]" />
        <div className="pointer-events-none absolute bottom-0 right-0 w-[26rem] h-[26rem] rounded-full bg-sirius-600/30 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)", backgroundSize: "44px 44px" }}
        />

        {/* Header marca */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white rounded-2xl p-2 shadow-spark">
            <img src={QuantusMark} alt="Quantus Invoice" className="w-11 h-11 object-contain" />
          </div>
          <div>
            <p className="text-white font-display font-bold text-lg leading-none">Quantus Invoice</p>
            <p className="text-white/45 text-[0.7rem] uppercase tracking-[0.18em] mt-1">A Sirius Solutions product</p>
          </div>
        </div>

        {/* Centro: tagline */}
        <div className="relative z-10 max-w-md">
          {/* Badge SENIAT: visible solo si la config del front lo habilita.
              El registro ante SENIAT es un proceso administrativo aún no
              completado → REACT_APP_SHOW_SENIAT_BADGE controla su visibilidad. */}
          {String(process.env.REACT_APP_SHOW_SENIAT_BADGE).toLowerCase() === "true" && (
            <span className="sirius-badge mb-5">
              <i className="fas fa-shield-halved" /> Certificado SENIAT
            </span>
          )}
          <h1 className="text-white font-display font-bold text-4xl xl:text-5xl leading-[1.1]">
            Iluminando el futuro con{" "}
            <span className="text-gradient-sirius">soluciones inteligentes</span>
          </h1>
          <p className="text-white/55 text-base mt-5 leading-relaxed">
            Facturación electrónica digital, multimoneda y conforme a SENIAT.
            Gestiona proformas, facturas y reportes desde un solo lugar.
          </p>

          {/* Mini stats glass */}
          <div className="grid grid-cols-3 gap-3 mt-8">
            {[
              { n: "100%", l: "SENIAT" },
              { n: "Multi", l: "Moneda" },
              { n: "24/7", l: "Disponible" },
            ].map((s) => (
              <div key={s.l} className="glass-panel rounded-xl px-3 py-3 text-center">
                <p className="text-white font-display font-bold text-lg leading-none">{s.n}</p>
                <p className="text-white/45 text-[0.62rem] uppercase tracking-wider mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p className="relative z-10 text-white/35 text-xs">
          © {new Date().getFullYear()} Sirius Solutions. Todos los derechos reservados.
        </p>
      </section>

      {/* ===================== PANEL DERECHO (formulario) ===================== */}
      <section className="relative flex items-center justify-center p-6 sm:p-10" style={{ background: "#f5f6fb" }}>
        <div className="w-full max-w-md">
          {/* Logo grande */}
          <div className="flex flex-col items-center mb-8">
            <img src={QuantusLogo} alt="Quantus Invoice" className="h-16 md:h-20 object-contain" />
            <p className="lg:hidden text-ink-400 text-xs mt-3 tracking-wide">
              A Sirius Solutions product
            </p>
          </div>

          <div className="sirius-card-elevated p-8">
            <div className="mb-6">
              <h2 className="font-display font-bold text-2xl text-ink-900">Bienvenido de vuelta</h2>
              <p className="text-ink-500 text-sm mt-1">Ingresa tus credenciales para continuar</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="mb-4">
                <label className="sirius-label">Email</label>
                <div className="relative">
                  <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                  <input
                    type="email"
                    placeholder="correo@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="sirius-input pl-11"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="mb-5">
                <label className="sirius-label">Contraseña</label>
                <div className="relative">
                  <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="sirius-input pl-11 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-sirius-600 hover:bg-sirius-50 transition-colors"
                    data-tooltip={showPassword ? "Ocultar" : "Mostrar"}
                    data-tooltip-pos="left"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"} text-sm`} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="sirius-btn-primary w-full py-3.5 shine disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <><i className="fas fa-circle-notch fa-spin" /> Ingresando…</>
                ) : (
                  <><i className="fas fa-arrow-right-to-bracket" /> Entrar</>
                )}
              </button>
            </form>

            <div className="text-center mt-5">
              <button
                type="button"
                onClick={() => {
                  setForgotEmail(email);
                  setShowForgotModal(true);
                }}
                className="text-ink-500 hover:text-sirius-700 text-sm font-medium transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </div>

          <p className="text-center text-ink-400 text-xs mt-6">
            Quantus Invoice v{process.env.REACT_APP_VERSION || "2.0"} · Sirius Solutions
          </p>
        </div>
      </section>

      {/* ========================= */}
      {/* FORGOT PASSWORD MODAL */}
      {/* ========================= */}
      {showForgotModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(13,13,16,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
        >
          <div className="bg-white rounded-2xl shadow-sirius-lg border border-slate-200/70 w-full max-w-md p-7 animate-sirius-fade-up">
            <div className="flex items-start gap-4 mb-5">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-sirius-50 text-sirius-600 flex items-center justify-center text-xl">
                <i className="fas fa-key" />
              </div>
              <div>
                <h2 className="text-lg font-display font-bold text-ink-900">Recuperar contraseña</h2>
                <p className="text-ink-500 text-sm mt-1">Te enviaremos un enlace para restablecerla.</p>
              </div>
            </div>

            <form onSubmit={handleForgotPassword}>
              <label className="sirius-label">Email</label>
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="sirius-input"
                placeholder="tu@email.com"
              />

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotModal(false);
                    setForgotMessage(null);
                  }}
                  className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold text-ink-700 bg-slate-100 hover:bg-slate-200 transition-all"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={loadingForgot}
                  className="sirius-btn-primary px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingForgot ? "Enviando…" : "Enviar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
